/**
 * DenSwitcherPill v0.1
 *
 * Top-right header pill that displays the user's currently-active Den
 * and lets them switch between their active Dens, add a new Den (via the
 * Den-type catalog chooser, opened in place), or flip into "Show all tools"
 * mode (the escape hatch for users who want the full sidebar regardless of Den).
 *
 * States:
 *   - Loading              → tiny placeholder (silent until data arrives)
 *   - Zero-Den             → "Den" CTA → /dashboard/dens/new
 *   - All tools            → "All tools" pill with dropdown to switch
 *                            back to a real Den
 *   - Normal (1+ Dens)     → tinted pill showing the active Den name
 *                            + dropdown to switch, add, or escape-hatch
 *
 * Sidebar refresh: every state change fires window.dispatchEvent(
 *   new CustomEvent('den-state-changed')) so AppSidebar re-queries
 *   user_active_dens + re-filters its nav items immediately.
 *
 * @version v0.1 (2026-05-17)
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Plus, Eye, EyeOff, Sparkles, Layers } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useRecordMode, withRecordParam } from "@/hooks/useRecordMode";
import { DenSwitcherModal } from "@/components/den-switcher/DenSwitcherModal";

interface DenRow {
  persona_key: string;
  is_primary: boolean;
}

interface DenCatalogEntry {
  persona_key: string;
  display_name: string;
  tint_class: string | null;
  icon_name: string | null;
}

const FALLBACK_TINT = "bg-zinc-800/60 text-zinc-200 border-zinc-700/50 hover:bg-zinc-800/80";
const ALL_SURFACES_TINT = "bg-amber-950/40 text-amber-200 border-amber-800/40 hover:bg-amber-900/50";

export function DenSwitcherPill() {
  const navigate = useNavigate();
  // RECORD-MODE-PARAM-PERSISTENCE (2026-05-17): navigate() calls inside
  // this dropdown drop ?rec=1 unless we re-append it. Wrap every Den-
  // routing target with withRecordParam().
  const recordMode = useRecordMode();
  // Den routing targets. These used to point at "/dens", which has no route
  // registered → both the pill's "Add another Den" and "all Dens" links 404'd.
  const goDens = () => navigate(withRecordParam("/dashboard/dens/new", recordMode));
  const goMyDens = () => navigate(withRecordParam("/dashboard/dens", recordMode));
  const [userId, setUserId] = useState<string | null>(null);
  const [activeDens, setActiveDens] = useState<DenRow[]>([]);
  const [catalog, setCatalog] = useState<Record<string, DenCatalogEntry>>({});
  const [showAll, setShowAll] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  // CHOOSE-DEN-TYPE (2026-07): the 27-persona catalog chooser lives in
  // DenSwitcherModal (restored from the removed TopBar wiring). "Add a Den —
  // choose type" in the pulldown opens it in place (overlay, no navigation).
  const [catalogOpen, setCatalogOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load session, dens, catalog, and show-all flag.
  const loadState = async () => {
    const { data: sess } = await supabase.auth.getSession();
    const uid = sess?.session?.user?.id || null;
    setUserId(uid);
    if (!uid) {
      setActiveDens([]);
      setLoaded(true);
      return;
    }

    const [dens, cat, prefs] = await Promise.all([
      supabase
        .from("user_active_dens" as any)
        .select("persona_key, is_primary")
        .eq("user_id", uid),
      supabase
        .from("den_catalog" as any)
        .select("persona_key, display_name, tint_class, icon_name"),
      supabase
        .from("user_preferences" as any)
        .select("show_all_surfaces")
        .eq("user_id", uid)
        .maybeSingle(),
    ]);

    setActiveDens(((dens.data as any) || []) as DenRow[]);
    const catMap: Record<string, DenCatalogEntry> = {};
    for (const row of (((cat.data as any) || []) as DenCatalogEntry[])) {
      catMap[row.persona_key] = row;
    }
    setCatalog(catMap);
    setShowAll(!!(prefs.data as any)?.show_all_surfaces);
    setLoaded(true);
  };

  useEffect(() => {
    loadState();
    const onChange = () => loadState();
    window.addEventListener("den-state-changed", onChange);
    return () => window.removeEventListener("den-state-changed", onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Outside-click close.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Flip primary to chosen Den + clear show-all. Fires den-state-changed.
  async function setPrimaryDen(personaKey: string) {
    if (!userId || switching) return;
    setSwitching(true);
    try {
      // Demote all rows then promote the chosen one. If the user doesn't
      // already have a row for this persona, insert one.
      await supabase
        .from("user_active_dens" as any)
        .update({ is_primary: false })
        .eq("user_id", userId);

      const existing = activeDens.find((d) => d.persona_key === personaKey);
      if (existing) {
        await supabase
          .from("user_active_dens" as any)
          .update({ is_primary: true })
          .eq("user_id", userId)
          .eq("persona_key", personaKey);
      } else {
        await supabase
          .from("user_active_dens" as any)
          .insert({ user_id: userId, persona_key: personaKey, is_primary: true });
      }

      await supabase
        .from("user_preferences" as any)
        .upsert(
          { user_id: userId, show_all_surfaces: false, updated_at: new Date().toISOString() },
          { onConflict: "user_id" },
        );

      setOpen(false);
      window.dispatchEvent(new CustomEvent("den-state-changed"));
      await loadState();
    } finally {
      setSwitching(false);
    }
  }

  // Toggle "Show all surfaces" — keeps existing primary, just flips the flag.
  async function toggleShowAll() {
    if (!userId) return;
    const next = !showAll;
    await supabase
      .from("user_preferences" as any)
      .upsert(
        { user_id: userId, show_all_surfaces: next, updated_at: new Date().toISOString() },
        { onConflict: "user_id" },
      );
    setShowAll(next);
    setOpen(false);
    window.dispatchEvent(new CustomEvent("den-state-changed"));
  }

  if (!loaded) {
    // Avoid flicker — render a thin placeholder until the first load completes.
    return <div style={{ height: 30, width: 110 }} aria-hidden />;
  }

  // Zero-Den state → generic "Den" chip. Clickable so the user can choose/create
  // a Den later (the left nav shows Engagement HQ in place of "The Den").
  if (activeDens.length === 0) {
    return (
      <button
        onClick={() => goDens()}
        className={cn("hidden md:flex items-center gap-1.5 transition-colors border rounded-md", FALLBACK_TINT)}
        title="Pick or create a Den to tune your dashboard to your work"
        style={{
          height: 30,
          padding: "0 12px",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        <Sparkles style={{ width: 13, height: 13 }} />
        Den
      </button>
    );
  }

  const primary = activeDens.find((d) => d.is_primary) || activeDens[0];
  const secondaries = activeDens.filter((d) => d.persona_key !== primary.persona_key);
  const primaryCat = catalog[primary.persona_key];
  const primaryName = primaryCat?.display_name || primary.persona_key;
  const primaryTint = primaryCat?.tint_class || FALLBACK_TINT;

  const showAllMode = showAll;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        title={showAllMode ? "Showing all tools — click to switch Den" : `Active Den: ${primaryName}`}
        className={cn(
          "hidden md:flex items-center gap-1.5 transition-colors border rounded-md",
          showAllMode ? ALL_SURFACES_TINT : primaryTint,
        )}
        style={{
          height: 30,
          padding: "0 10px",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {showAllMode ? (
          <Layers style={{ width: 13, height: 13 }} />
        ) : (
          <span aria-hidden style={{ fontSize: 13, lineHeight: 1 }}>{personaEmoji(primary.persona_key)}</span>
        )}
        <span>{showAllMode ? "All tools" : primaryName}</span>
        {!showAllMode && secondaries.length > 0 && (
          <span className="flex items-center gap-0.5 ml-0.5">
            {secondaries.slice(0, 2).map((d) => (
              <span
                key={d.persona_key}
                aria-hidden
                title={catalog[d.persona_key]?.display_name || d.persona_key}
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: 999,
                  background: "currentColor",
                  opacity: 0.6,
                  display: "inline-block",
                }}
              />
            ))}
          </span>
        )}
        <ChevronDown style={{ width: 12, height: 12, opacity: 0.7 }} />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 260,
            background: "#131820",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 8,
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
            zIndex: 100,
            overflow: "hidden",
          }}
        >
          {/* Active Den row (current primary) */}
          {!showAllMode && (
            <div style={{ padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Active Den
              </p>
              <p style={{ fontSize: 13, color: "white", fontWeight: 600, marginTop: 2 }}>
                {personaEmoji(primary.persona_key)} {primaryName}
              </p>
            </div>
          )}

          {/* Secondary Dens — one-click switch */}
          {secondaries.length > 0 && (
            <>
              <div style={{ padding: "8px 14px 4px", fontSize: 10, color: "rgba(255,255,255,0.4)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Switch to
              </div>
              {secondaries.map((d) => {
                const cat = catalog[d.persona_key];
                return (
                  <button
                    key={d.persona_key}
                    onClick={() => setPrimaryDen(d.persona_key)}
                    disabled={switching}
                    className="w-full text-left flex items-center gap-2 hover:bg-primary/10 transition-colors"
                    style={{
                      padding: "8px 14px",
                      fontSize: 13,
                      color: "rgba(255,255,255,0.85)",
                      border: "none",
                      background: "none",
                      cursor: switching ? "default" : "pointer",
                    }}
                  >
                    <span aria-hidden>{personaEmoji(d.persona_key)}</span>
                    <span>{cat?.display_name || d.persona_key}</span>
                  </button>
                );
              })}
            </>
          )}

          {/* All-surfaces mode → restore the active Den */}
          {showAllMode && (
            <button
              onClick={() => setPrimaryDen(primary.persona_key)}
              disabled={switching}
              className="w-full text-left flex items-center gap-2 hover:bg-primary/10 transition-colors"
              style={{
                padding: "10px 14px",
                fontSize: 13,
                color: "rgba(255,255,255,0.85)",
                border: "none",
                background: "none",
                cursor: switching ? "default" : "pointer",
              }}
            >
              <span aria-hidden>{personaEmoji(primary.persona_key)}</span>
              <span>Switch back to {primaryName}</span>
            </button>
          )}

          {/* Add a Den — opens the Den-type catalog chooser IN PLACE (overlay),
              never navigates away. Restored from the removed DenSwitcherModal
              wiring so all ~27 Den types are pickable from the pulldown again. */}
          <button
            onClick={() => {
              setOpen(false);
              setCatalogOpen(true);
            }}
            title="Browse all Den types and add one — Founder, Investor, Recruiter, Journalist and more"
            className="w-full text-left flex items-center gap-2 hover:bg-primary/10 transition-colors"
            style={{
              padding: "10px 14px",
              fontSize: 13,
              color: "rgba(255,255,255,0.75)",
              borderTop: "1px solid rgba(255,255,255,0.06)",
              borderLeft: "none",
              borderRight: "none",
              borderBottom: "none",
              background: "none",
              cursor: "pointer",
            }}
          >
            <Plus style={{ width: 14, height: 14 }} /> Add a Den — choose type
          </button>

          {/* Show-all toggle. Real behavior: shows EVERY tool in the left nav
              regardless of the active Den's tailored subset (user_preferences
              .show_all_surfaces). Labeled in plain English — no dev jargon. */}
          <button
            onClick={toggleShowAll}
            title={showAllMode
              ? "Go back to just the tools your active Den needs"
              : "Show every tool in the menu, not only your active Den's"}
            className="w-full text-left flex items-center gap-2 hover:bg-primary/10 transition-colors"
            style={{
              padding: "10px 14px",
              fontSize: 13,
              color: "rgba(255,255,255,0.75)",
              border: "none",
              background: "none",
              cursor: "pointer",
            }}
          >
            {showAllMode ? (
              <>
                <EyeOff style={{ width: 14, height: 14 }} /> Show only my Den's tools
              </>
            ) : (
              <>
                <Eye style={{ width: 14, height: 14 }} /> Show all tools
              </>
            )}
          </button>

          {/* Show all of the user's active Dens */}
          <a
            href={withRecordParam("/dashboard/dens", recordMode)}
            onClick={(e) => {
              e.preventDefault();
              setOpen(false);
              goMyDens();
            }}
            style={{
              display: "block",
              padding: "10px 14px",
              borderTop: "1px solid rgba(255,255,255,0.06)",
              fontSize: 12,
              color: "rgba(255,255,255,0.5)",
              textDecoration: "none",
            }}
          >
            Show all my Dens →
          </a>
        </div>
      )}

      {/* Den-type catalog chooser — overlay, opens in place. mode="add" inserts
          the chosen persona as a non-primary Den (keeps the current primary). */}
      <DenSwitcherModal open={catalogOpen} mode="add" onClose={() => setCatalogOpen(false)} />
    </div>
  );
}

// Tiny emoji map per persona — keeps the pill compact without a Lucide icon
// rendering cost. Falls back to a generic dot for unknown keys.
function personaEmoji(key: string): string {
  const map: Record<string, string> = {
    founder: "🚀",
    athlete: "🏅",
    recruiter: "🎯",
    physician: "🩺",
    lawyer: "⚖️",
    investor: "💼",
    author: "📚",
    politician: "🏛️",
    activist: "📣",
    religious: "🕊️",
    real_estate_agent: "🏠",
    sales_pro: "📈",
    coach: "🧭",
    speaker: "🎤",
    fractional_executive: "🧑‍💼",
    financial_advisor: "💰",
    consultant: "📊",
    podcast_host: "🎧",
    real_estate_investor: "🏢",
    course_creator: "🎓",
    creative_freelancer: "🎨",
    therapist: "💗",
    executive: "🏢",
    journalist: "📰",
    academic: "🎓",
    artist: "🖌️",
    military: "🛡️",
  };
  return map[key] || "●";
}
